import React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
  Checkbox,
  Button,
  Dropdown,
  TextField,
  Autocomplete,
  RadioGroup,
  RadioButton,
} from '@goguardian/based';

import Loading from '_shared/components/loading';
import { track } from '../../../track';
import { countries } from 'country-data';
import _ from 'lodash';
import sflinkClient from '_shared/lib/sflink-client';
import { ALLOWED_COUNTRIES, getCountryName } from '_shared/lib/countries';
import US_STATES from '_shared/lib/usa-states';
import JOB_TITLES from '_shared/lib/job-titles';
import BlockedCountryWarning from './blockedCountryWarning';
import style from './style.scss';

const TOS_URL = 'https://www.goguardian.com/policies/eula';
const PRIVACY_POLICY_URL = 'https://www.goguardian.com/policies/privacy';

const CA_PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon Territory' },
];

class CreateAccount extends React.Component {
  constructor(...args) {
    super(...args);

    this.countries = this.createCountrySource();

    this.state = {
      doSave: false,
      params: {
        orgName: this.props.orgName,
        zip: this.props.zip,
        country: this.props.country,
        firstName: this.props.firstName,
        lastName: this.props.lastName,
        phoneNumber: this.props.phoneNumber,
        email: this.props.email,
        jobTitle: 'Tech Admin / IT Director',
        password: '',
        terms: false,
        province: 'AB',
        usState: 'AL',
        gdpr: null,
      },
      formErrors: [],
      orgInfo: {},
      query: '',
      invalidPass: false,
      selectedCountryName: 'United States',
    };

    this.handleChange = this.handleChange.bind(this);
    this.getOrganizations = this.getOrganizations.bind(this);
  }

  passwordValid(password) {
    if (password.length < 8) {
      return false;
    }

    // check lowercase
    if (!/[a-z]/.test(password)) {
      return false;
    }
    // check uppercase
    if (!/[A-Z]/.test(password)) {
      return false;
    }
    // check digit
    if (!/\d/.test(password)) {
      return false;
    }
    // check special character
    if (!/[$@!%*?&]/.test(password)) {
      return false;
    }
    return true;
  }

  componentDidMount() {
    track('signupFlow', {
      step: '/create-account',
      productId: this.props.productId,
    });
  }

  createCountrySource() {
    const formattedCountries = [];
    let hometown = {};

    countries.all.forEach((c) => {
      const value = c.alpha2.toUpperCase();
      const label = c.name;

      if (c.status !== 'reserved') {
        // not ISO friendly
        if (value === 'US') {
          hometown = {
            value,
            label,
          };
        } else {
          formattedCountries.push({
            value,
            label,
          });
        }
      }
    });

    const sorted = _.sortBy(formattedCountries, 'label');
    sorted.unshift(hometown); // Make US first in dropdown
    return sorted;
  }

  updateField(fieldName, value) {
    if (
      typeof value === 'string' &&
      fieldName != 'firstName' &&
      fieldName != 'lastName' &&
      fieldName != 'zip' &&
      fieldName != 'orgName'
    ) {
      value = value.trim();
    }

    this.setState({
      params: {
        ...this.state.params,
        [fieldName]: value,
      },
    });
  }

  updateCountry(country) {
    this.setState({
      selectedCountryName: getCountryName(this.countries, country),
      params: {
        ...this.state.params,
        country,
      },
    });
  }

  renderError() {
    let errorText = '';

    if (this.props.createAccount && this.props.createAccount.error) {
      const error = this.props.createAccount.error;

      errorText = error.detail || error.name;
      if (
        error.name === 'ValidationError' &&
        error.details &&
        error.details.length > 0 &&
        error.details[0].message
      ) {
        errorText = error.details[0].message;
      }

      return <div className={style.error}>{errorText}</div>;
    } else if (this.state.formErrors.length > 0) {
      errorText = `Please check the following fields: ${this.state.formErrors.join(
        ', '
      )}`;
      return <div className={style.error}>{errorText}</div>;
    } else {
      return;
    }
  }

  validateAndSubmit(e) {
    const { params } = this.state;
    const formErrors = [];
    const fieldNames = {
      orgName: 'Organization Name',
      zip: 'ZIP/Postal Code',
      country: 'Country',
      province: 'Province',
      usState: 'State',
      firstName: 'First Name',
      lastName: 'Last Name',
      phoneNumber: 'Phone Number',
      email: 'Email Address',
      password: 'Password',
      jobTitle: 'Job Title',
      terms: 'Terms and Privacy Agreement',
      gdpr: 'Consent to receive emails',
    };
    e.preventDefault();

    Object.keys(params).forEach((field) => {
      // Format from camelCase to Camel Case
      const fieldName = fieldNames[field];

      switch (field) {
        case 'phoneNumber':
          // first strip all whitespace
          const phone = params[field].replace(/\s/g, '');

          // phone can contain many types of delimiters and allow exts
          const validPhone = new RegExp(/^[0-9().+\-/(ext)]+$/gi);

          if (!phone.match(validPhone) || phone.length === 0) {
            formErrors.push(fieldName);
          }

          if (phone.length < 7) {
            formErrors.push('Phone number must be more than 7 characters');
          }

          if (phone.length > 20) {
            formErrors.push('Phone number must be fewer than 20 characters');
          }

          break;
        case 'country':
          if (params.country.length !== 2) {
            formErrors.push(fieldName);
          }

          break;

        case 'password':
          if (!this.passwordValid(params[field])) {
            formErrors.push(fieldName);
            this.setState({ invalidPass: true });
          } else {
            this.setState({ invalidPass: false });
          }

          break;

        case 'province':
          if (params.country === 'CA' && params.province.length !== 2) {
            formErrors.push(fieldName);
          }

          break;

        case 'usState':
          if (params.country === 'US' && params.usState.length !== 2) {
            formErrors.push(fieldName);
          }

          break;

        case 'gdpr':
          if (params.gdpr === null) {
            formErrors.push(fieldName);
          }

          break;

        case 'firstName':
        case 'lastName':
          if (params[field].length === 0) {
            formErrors.push(fieldName);
          }

          break;

        default:
          if (
            !params[field] ||
            params[field] === '' ||
            params[field].length < 4 ||
            params[field].length > 100
          ) {
            formErrors.push(fieldName);
          }
      }
    });

    if (formErrors.length === 0) {
      this.hubspotHandler();
    }

    this.setState({
      formErrors: formErrors,
      doSave: formErrors.length === 0 ? true : false,
    });
  }

  handleChange(option) {
    // Retrieve option information
    const info = this.state.orgInfo[option.value];

    // Setup new parameters object
    const params = {
      ...this.state.params,
      zip: info.postalCode,
      orgName: info.name,
      phoneNumber: info.phone,
    };

    // Define the label based on if state and city being available
    let label = `${info.name} (${info.city}, ${info.state})`;
    if (!info.city || !info.state) label = info.name;

    // Update state
    this.setState({
      params,
      orgSearchValue: {
        label,
        value: info.id,
      },
    });
  }

  getOrganizations(input) {
    // Set default variables
    const options = [];
    const orgInfo = {};

    // Update state's query and search values
    this.setState({
      query: input,
      orgSearchValue: {
        label: input,
        value: null,
      },
      params: {
        ...this.state.params,
        orgName: input,
      },
    });

    // Begin getting orgs list
    return new Promise((resolve, reject) => {
      // Check if input is at least 3 characters long
      if (!input || input.length < 3) {
        resolve(options);
      } else {
        // Send request to api with input
        return sflinkClient
          .get(`/v1/organizations/search?name=${input}`)
          .then((response) => {
            const orgs = response.organizations || [];

            if (orgs.length) {
              orgs.forEach((item) => {
                // Setup the label for the option
                let label = `${item.name} (${item.city}, ${item.state})`;
                if (!item.city || !item.state) label = item.name;

                // Push the option to the array
                options.push({
                  value: item.id,
                  label,
                });

                // Add org info item
                orgInfo[item.id] = item;
              });
            }

            // Set orgInfo object in state, and resolve promise.
            this.setState({ orgInfo }, () => {
              resolve(options);
            });
          });
      }
    });
  }

  termsClickHandler(e, url) {
    e.stopPropagation();
    e.preventDefault();
    if (url) window.open(url);
  }

  renderForm() {
    let blockedCountry = !ALLOWED_COUNTRIES.includes(this.state.params.country);

    return (
      <div className={style.createAccountContainer}>
        <h1>Create your GoGuardian Account</h1>
        <p>Welcome to GoGuardian!</p>
        <p>Please create an account to continue.</p>
        <form
          method='post'
          ref='createAccountForm'
          onSubmit={this.validateAndSubmit.bind(this)}
        >
          <div className={style.accountForm}>
            <Dropdown
              label='Country'
              className={style.dropdown}
              source={this.countries}
              value={this.state.params.country}
              onChange={(country) => this.updateCountry(country)}
              block
            />
            {this.state.params.country === 'US' ? (
              <Autocomplete
                label='Organization&#39;s Name'
                source={this.getOrganizations}
                onChange={this.handleChange}
                query={this.state.query}
                className={style.autoCompleteField}
                value={this.state.orgSearchValue}
              />
            ) : (
              <TextField
                label='Organization&#39;s Name'
                value={this.props.orgName || this.state.params.orgName || ''}
                className={style.textField}
                onChange={(value) => this.updateField('orgName', value)}
                block
              />
            )}
            {this.state.params.country === 'CA' && (
              <Dropdown
                label='Province'
                className={style.dropdown}
                source={CA_PROVINCES}
                value={this.state.params.province}
                onChange={(value) => this.updateField('province', value)}
                block
              />
            )}
            {this.state.params.country === 'US' && (
              <Dropdown
                label='State'
                className={style.dropdown}
                source={US_STATES}
                value={this.state.params.usState}
                onChange={(value) => this.updateField('usState', value)}
                block
              />
            )}
            <TextField
              label='ZIP/Postal Code'
              className={style.textField}
              onChange={(value) => this.updateField('zip', value)}
              value={this.state.params.zip}
              block
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <TextField
                label='First Name'
                value={this.state.params.firstName}
                className={style.textField}
                onChange={(value) => this.updateField('firstName', value)}
              />
              <TextField
                label='Last Name'
                value={this.state.params.lastName}
                className={style.textField}
                onChange={(value) => this.updateField('lastName', value)}
              />
            </div>
            <Dropdown
              label='Job Title'
              value={this.state.params.jobTitle}
              source={JOB_TITLES}
              className={style.dropdown}
              onChange={(value) => this.updateField('jobTitle', value)}
              block
            />
            <TextField
              label='Phone Number'
              value={this.state.params.phoneNumber}
              className={style.textField}
              onChange={(value) => this.updateField('phoneNumber', value)}
              block
            />
            <TextField
              label='Google Apps Email'
              disabled={true}
              value={this.props.email}
              className={style.textField}
              onChange={(value) => this.updateField('email', value)}
              block
            />
            <TextField
              label='Enter A New Password'
              value={this.state.params.password}
              className={style.textField}
              onChange={(value) => this.updateField('password', value)}
              type='password'
              block
            />
          </div>
          <div className={this.state.invalidPass ? style.error : undefined}>
            <p>Please make sure your password contains the following:</p>
            <ul className={style.passwordRequirements}>
              <li>Must be 8 characters long</li>
              <li>A capital letter</li>
              <li>A lowercase letter</li>
              <li>A number</li>
              <li>At least one of the following characters (@,$,!,%,*,?,&)</li>
            </ul>
          </div>
          <div className={style.termsRow}>
            <div className={style.gdpr}>
              <RadioGroup onChange={(value) => this.updateField('gdpr', value)}>
                <RadioButton
                  value={true}
                  label='Yes, I do consent to receiving additional information about GoGuardian&#39;s products and services.'
                />
                <RadioButton
                  value={false}
                  label='No, I do not consent to receiving additional information about GoGuardian&#39;s products and services.'
                />
              </RadioGroup>
            </div>
            <div className={style.termsAgreement}>
              <Checkbox
                onChange={(value) => this.updateField('terms', value)}
                label={
                  <span>
                    I agree to the{' '}
                    <a
                      onClick={(e) => this.termsClickHandler(e, TOS_URL)}
                      href={TOS_URL}
                      target='_blank'
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      onClick={(e) =>
                        this.termsClickHandler(e, PRIVACY_POLICY_URL)
                      }
                      href={PRIVACY_POLICY_URL}
                      target='_blank'
                    >
                      Privacy Policy
                    </a>
                  </span>
                }
              />
            </div>
            {this.renderError()}
            <Button disabled={blockedCountry}>Create my account</Button>
            {blockedCountry && (
              <BlockedCountryWarning name={this.state.selectedCountryName} />
            )}
          </div>
        </form>
      </div>
    );
  }

  renderLoader() {
    return (
      <Loading
        actionName='createAccount'
        actionParams={[this.state.params]}
        onComplete={this.loaderComplete.bind(this)}
      >
        We are creating your account...
      </Loading>
    );
  }

  /**
   * When the account is created, this method is called.
   * We want to track our lead and associate them
   * as a known lead with their details.
   */
  loaderComplete() {
    this.trackLead();
  }

  /**
   * Get user data from state or props.
   * State will be used if there is no data for the user in props.
   */
  getUserData() {
    const { props, state } = this;

    return {
      firstName: props.firstName || state.params.firstName,
      lastName: props.lastName || state.params.lastName,
      email: props.email || state.params.email,
      company: props.orgName || state.params.orgName,
      phone: props.phoneNumber || state.params.phoneNumber,
      address: {
        country: props.country || state.params.country,
        postalCode: props.zip || state.params.zip,
      },
    };
  }

  /**
   * Track our lead with user details from props or state in Segment.
   */
  trackLead() {
    if (window.analytics) {
      // Track custom activity which is mapped in Segment
      const traits = Object.assign(
        {},
        analytics.user()._getTraits(),
        this.getUserData()
      );
      analytics.track('signupFlow', traits);

      // Identify anonymous user with user details
      analytics.identify(analytics.user().anonymousId(), this.getUserData());

      this.setState({
        doSave: false,
      });
    }
  }

  /**
   * Send lead data to Hubspot without interrupting app flow
   */
  hubspotHandler() {
    const {
      selectedCountryName,
      params: {
        email,
        orgName,
        zip,
        firstName,
        lastName,
        country,
        usState,
        province,
        phoneNumber,
        gdpr,
      },
    } = this.state;

    const gdprAgreement =
      gdpr === true
        ? "Yes, I do consent to receiving additional information about GoGuardian's products and services."
        : "No, I do not consent to receiving additional information about GoGuardian's products and services.";

    const stateOrProvince = () => {
      if (country === 'US') {
        return US_STATES.find(({ value }) => usState === value).label;
      }

      if (country === 'CA') {
        return CA_PROVINCES.find(({ value }) => province === value).label;
      }

      return '';
    };

    const hsCookie = () => {
      const cookie = document.cookie
        .split('; ')
        .find((c) => c.startsWith('hubspotutk'));

      if (!cookie) {
        return null;
      }

      return cookie.split('=')[1];
    };

    const baseUrl = 'https://api.hsforms.com/submissions/v3/integration/submit';
    const hsPortalId = '5258344';
    const hsFormId = '5c843147-48b8-4e48-9d16-62d087f41141';

    const reqBody = {
      fields: [
        { name: 'firstname', value: firstName },
        { name: 'lastname', value: lastName },
        { name: 'company', value: orgName },
        { name: 'country_region_2', value: selectedCountryName },
        { name: 'state_region_2', value: stateOrProvince() },
        { name: 'phone', value: phoneNumber },
        { name: 'email', value: email },
        { name: 'zip', value: zip },
      ],
      context: {
        hutk: hsCookie(),
        pageUri: window.location.href,
        pageName: 'GoGuardian Signup: Admin',
      },
      legalConsentOptions: {
        consent: {
          consentToProcess: gdpr,
          text: gdprAgreement,
          communications: [
            {
              value: gdpr,
              subscriptionTypeId: 18701957,
              text: gdprAgreement,
            },
          ],
        },
      },
    };

    const hubspotRequest = new Request(`${baseUrl}/${hsPortalId}/${hsFormId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reqBody),
    });

    fetch(hubspotRequest)
      .then((res) => res.json())
      .then((res) => console.log('Response: ', res))
      .catch((e) => console.error('Error: ', e));
  }

  render() {
    return this.state.doSave ? this.renderLoader() : this.renderForm();
  }
}

const mapStateToProps = (state) => {
  let email = '';
  let firstName = _.get(state, 'getGoogleInfo.data.firstName', '');
  let lastName = _.get(state, 'getGoogleInfo.data.lastName', '');
  let orgName = _.get(state, 'getGoogleInfo.data.orgName', '');
  let phoneNumber = _.get(state, 'getGoogleInfo.data.phoneNumber', '');
  let country = _.get(state, 'getGoogleInfo.data.address.countryCode', 'US');
  let zip = _.get(state, 'getGoogleInfo.data.address.postalCode', '');

  if (state.getGoogleInfo) {
    email = state.getGoogleInfo.data.email;
  } else {
    email = state.checkNoAuth && state.checkNoAuth.email;
  }

  return {
    productId: state.setProduct,
    email,
    firstName,
    lastName,
    orgName,
    phoneNumber,
    country,
    zip,
    createAccount: state.createAccount,
  };
};

export default connect(mapStateToProps)(withRouter(CreateAccount));



/** WEBPACK FOOTER **
 ** ./src/app/components/createAccount/index.jsx
 **/